"use client";

import Link from "next/link";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { useT } from "@/lib/i18n/context";
import { productCardTranslations } from "./ProductCard.i18n";
import styles from "./ProductCard.module.css";

/**
 * Shape wymagany przez `ProductCard`. Structurally compatible z
 * `CShopCategoryProduct` (i podobnymi typami produktow z innych sklepowych
 * endpointow) - caller moze przekazac dowolny obiekt z tymi polami.
 */
export type ProductCardItem = {
  id: number;
  name: string;
  href: string;
  status: "public" | "temporary_unavailable" | "archive" | "in_set";
  badge: "bestseller" | "recomended" | "new" | null;
  category_name: string;
  price_brutto: number | null;
  price_brutto_before_promotion: number | null;
  promocja: boolean;
  lowest_price: number | null;
  is_variant_min_price: boolean;
  picture: {
    thumb_src: string;
    alt: string;
    width: number | null;
    height: number | null;
  } | null;
};

export type ProductCardProps = {
  item: ProductCardItem;
  showPrices: boolean;
  currency: string;
};

export function ProductCard({ item, showPrices, currency }: ProductCardProps) {
  const t = useT(productCardTranslations);
  const href = extractInternalHref(item.href);
  const statusLabel = item.status !== "public" ? t.status[item.status] : null;

  const priceBlock = showPrices ? (
    <ProductPrice item={item} currency={currency} />
  ) : null;

  const cardBody = (
    <>
      <div className={styles.image}>
        {item.badge && (
          <span className={styles.badge} data-badge={item.badge}>
            {t.badges[item.badge]}
          </span>
        )}
        {item.picture?.thumb_src ? (
          <img
            src={item.picture.thumb_src}
            alt={item.picture.alt || item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            loading="lazy"
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      <div className={styles.details}>
        <h3 className={styles.name}>{item.name}</h3>
        {item.category_name && (
          <div className={styles.category}>{item.category_name}</div>
        )}
        {priceBlock}
        {statusLabel && <div className={styles.status}>{statusLabel}</div>}
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.link} aria-label={item.name}>
          {cardBody}
        </Link>
      ) : (
        <a href={item.href} className={styles.link} aria-label={item.name}>
          {cardBody}
        </a>
      )}
      {/* Przycisk koszyka wylaczamy gdy:
          - hide_prices extension (shop w trybie katalogu)
          - status !== public (temporary_unavailable / archive / in_set)
          - parent z wariantami (is_variant_min_price) - trzeba wejsc w karte
            i wybrac wariant
          Na liscie NIE mamy stock (drogie query per row), wiec sprawdzenie
          stock tylko na karcie produktu. */}
      {showPrices && item.status === "public" && !item.is_variant_min_price && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.addToCartButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.alert(t.cartComingSoon);
            }}
          >
            {t.addToCart}
          </button>
        </div>
      )}
    </li>
  );
}

function ProductPrice({
  item,
  currency,
}: {
  item: ProductCardItem;
  currency: string;
}) {
  const t = useT(productCardTranslations);
  if (item.price_brutto === null) return null;

  const mainFormatted = t.formatPrice(item.price_brutto, currency);

  if (item.promocja && item.price_brutto_before_promotion !== null) {
    const beforeFormatted = t.formatPrice(
      item.price_brutto_before_promotion,
      currency,
    );
    return (
      <div className={styles.priceWrap}>
        <span className={styles.pricePromo}>
          <span className={styles.priceBefore}>{beforeFormatted}</span>{" "}
          {item.is_variant_min_price && (
            <span className={styles.priceFrom}>{t.priceFrom}</span>
          )}{" "}
          {mainFormatted}
        </span>
        {item.lowest_price !== null && (
          <div className={styles.lowestPrice}>
            {t.lowestPrice(t.formatPrice(item.lowest_price, currency))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.priceWrap}>
      <span className={styles.price}>
        {item.is_variant_min_price && (
          <span className={styles.priceFrom}>{t.priceFrom}</span>
        )}{" "}
        {mainFormatted}
      </span>
    </div>
  );
}
